import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Space } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import {
  XMobDrawer,
  XMobHamburger,
  XMobHeader,
  XMobHeaderBar,
  XMobHeaderSide,
  XMobLogo,
  XMobMenuList,
  XMobMenuItem,
} from "./MobHeaderStyle";
import HeaderTop from "./HeaderTop";
import OrgSwitcher from "../OrgSwitcher/OrgSwitcher";
import { getSidebarMenu, getFirstAvailableRoute, isMultiOrgUser, TOPLINE_COUNT_EVENT } from "../../utils/permissions";
import useWindowWidth, { MOBILE_BREAKPOINT } from "../../hooks/useWindowWidth";
import {
  PoweredByWrapper,
  PoweredByBadge,
  VersionWrapper,
  VersionText,
} from "../Sidebar/SidebarStyle";

const MobHeader = () => {
  const location = useLocation();
  const orgLogo = useSelector(state => state.login?.user?.organization?.logo);
  const appVersion = localStorage.getItem('app_version') || null;
  const orgSettings = (() => {
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}');
      return {
        showPoweredBy: ud?.organization?.show_powered_by !== false,
        showVersion: ud?.organization?.show_version !== false,
      };
    } catch (e) { return { showPoweredBy: true, showVersion: true }; }
  })();
  const [orgLogoUrl, setOrgLogoUrl] = useState(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return userData?.organization?.logo?.downloadUrl || null;
    } catch (e) { return null; }
  });
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    if (orgLogo?.downloadUrl) {
      setOrgLogoUrl(orgLogo.downloadUrl);
      setLogoError(false);
    } else if (orgLogo === null || orgLogo === undefined) {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const lsUrl = userData?.organization?.logo?.downloadUrl || null;
        setOrgLogoUrl(lsUrl);
      } catch (e) { setOrgLogoUrl(null); }
    }
  }, [orgLogo]);
  const [open, setOpen] = useState(false);
  const showDrawer = () => {
    setOpen(!open);
  };
  const onClose = () => {
    setOpen(false);
  };

  // The drawer is portaled to document.body, so the `display: none` media query on
  // XMobHeader never reaches it. Without this, widening the window past the mobile
  // breakpoint leaves the drawer open underneath the desktop sidebar.
  const windowWidth = useWindowWidth();
  useEffect(() => {
    if (windowWidth > MOBILE_BREAKPOINT) setOpen(false);
  }, [windowWidth]);

  // The Topline entry comes and goes with its count (the desktop Sidebar
  // refreshes it); rebuild the menu when it changes.
  const [toplineTick, setToplineTick] = useState(0);
  useEffect(() => {
    const onCount = () => setToplineTick((t) => t + 1);
    window.addEventListener(TOPLINE_COUNT_EVENT, onCount);
    return () => window.removeEventListener(TOPLINE_COUNT_EVENT, onCount);
  }, []);

  // Menu mirrors desktop Sidebar: permission-filtered, ordered, auto-updates when ROUTES change
  const menuItems = useMemo(() => {
    return getSidebarMenu().map((route) => ({
      key: route.key,
      label: route.label,
      icon: route.icon,
      route: route.path,
    }));
  }, [toplineTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstAvailableRoutePath = useMemo(() => {
    const r = getFirstAvailableRoute();
    return r?.path || "/";
  }, [toplineTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeKey = useMemo(() => {
    const exact = menuItems.find((i) => i.route === location.pathname);
    if (exact) return exact.key;
    const prefix = menuItems.find(
      (i) => i.route !== "/" && location.pathname.startsWith(i.route)
    );
    if (prefix) return prefix.key;
    const root = menuItems.find((i) => i.route === "/");
    return root?.key;
  }, [location.pathname, menuItems]);

  return (
    <XMobHeader>
      <XMobHeaderBar>
        <XMobHeaderSide>
          <XMobHamburger onClick={showDrawer}>
            <MenuOutlined />
          </XMobHamburger>
          {/* Active organization initials, multi-org users only */}
          {isMultiOrgUser() && <OrgSwitcher collapsed inline />}
        </XMobHeaderSide>
        <XMobHeaderSide>
          <Link to={firstAvailableRoutePath}>
            <XMobLogo
              src={orgLogoUrl && !logoError ? orgLogoUrl : "/images/logo.svg"}
              alt="logo"
              onError={() => setLogoError(true)}
            />
          </Link>
        </XMobHeaderSide>
        <XMobHeaderSide className="right">
          <HeaderTop responsive />
        </XMobHeaderSide>
      </XMobHeaderBar>
      <XMobDrawer
        title={false}
        closable={{ "aria-label": "Close Button" }}
        onClose={onClose}
        open={open}
        placement="left"
        zIndex={90}
        width={250}
      >
        {/* Active organization indicator + switcher, multi-org users only */}
        {isMultiOrgUser() && (
          <div style={{ padding: "0 20px 12px" }}>
            <OrgSwitcher onNavigate={onClose} />
          </div>
        )}

        {/* Navigation Menu */}
        <XMobMenuList>
          {menuItems.map((item) => (
            <XMobMenuItem key={item.key} $active={activeKey === item.key}>
              <Link to={item.route} onClick={onClose}>
                <Space>
                  <img src={item.icon} alt={item.label} />
                  {item.label}
                </Space>
              </Link>
            </XMobMenuItem>
          ))}
        </XMobMenuList>

        {orgSettings.showPoweredBy && (
          <PoweredByWrapper style={{ padding: '16px 16px 4px' }}>
            <PoweredByBadge>
              <img src="/images/crowdvox-logo-purple.svg" alt="CrowdVox" />
            </PoweredByBadge>
          </PoweredByWrapper>
        )}
        {orgSettings.showVersion && appVersion && (
          <VersionWrapper>
            <VersionText>Version {appVersion}</VersionText>
          </VersionWrapper>
        )}
      </XMobDrawer>
    </XMobHeader>
  );
};

export default MobHeader;
